import { useChatStore } from './chatStore';
import api from '../../utils/api';

export class FlowEngine {
  static instance = null;
  nodes = [];
  edges = [];

  static getInstance() {
    if (!FlowEngine.instance) {
      FlowEngine.instance = new FlowEngine();
    }
    return FlowEngine.instance;
  }

  updateGraph(nodes, edges) {
    this.nodes = nodes;
    this.edges = edges;
  }

  async runFlow(startNodeId) {
    const store = useChatStore.getState();
    store.resetChat();
    store.setStatus('running');
    await this.executeNode(startNodeId);
  }

  async handleUserInput(text) {
    const store = useChatStore.getState();
    if (store.status !== 'paused') return;

    store.addHistoryItem({
      id: Math.random().toString(36).substr(2, 9),
      nodeId: store.currentNodeId || '',
      sender: 'user',
      type: 'text',
      content: text,
      timestamp: Date.now()
    });

    const currentNode = this.nodes.find(n => n.id === store.currentNodeId);
    if (!currentNode) return;
    const data = currentNode.data;

    store.updateVariable('lastUserInput', text);

    if (data.type === 'integration') {
      const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
      try {
        await api.post(`/widget/chat/message?ai_agent_id=${aiAgentId}`, {
          message: text,
          session_id: store.sessionId,
          user_id: store.sessionId,
          channel: 'widget'
        });
      } catch (e) {
        console.error('Failed to forward user input in simulator:', e);
      }
      return;
    }

    if (data.type === 'ai_agent') {
      store.setStatus('running');
      await this.executeAiTurn(currentNode.id, data, store);
      return;
    }

    if (data.type === 'message') {
      const nextId = this.resolveInputRoute(currentNode.id, text, useChatStore.getState().variables);
      if (nextId) {
        store.setStatus('running');
        await this.executeNode(nextId);
      }
      return;
    }

    if (data.type === 'input') {
      if (data.keywords) {
        const keywords = data.keywords.split(',').map(k => k.trim().toLowerCase()).filter(Boolean);
        if (!keywords.some(k => text.toLowerCase().includes(k))) {
          return;
        }
      }

      if (data.variableName) store.updateVariable(data.variableName, text);
      store.setStatus('running');

      const nextId = this.resolveInputRoute(currentNode.id, text, useChatStore.getState().variables);
      if (nextId) await this.executeNode(nextId);
      else store.setStatus('completed');
    }
  }

  async handleImageUpload(file) {
    const store = useChatStore.getState();
    if (store.status !== 'paused') return;

    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
    const MAX_SIZE = 5 * 1024 * 1024;

    const currentNode = this.nodes.find(n => n.id === store.currentNodeId);
    if (!currentNode) return;
    const data = currentNode.data;
    const varName = data.variableName || 'uploadedImage';

    if (!ALLOWED_TYPES.includes(file.type)) {
      store.updateVariable(`${varName}.url`, null);
      store.updateVariable(`${varName}.filename`, file.name);
      store.updateVariable(`${varName}.mimeType`, file.type);
      store.updateVariable(`${varName}.error`, 'invalid_file_type');
      store.addHistoryItem({
        id: Math.random().toString(36).substr(2, 9),
        nodeId: store.currentNodeId || '',
        sender: 'system',
        type: 'text',
        content: `❌ Invalid file type: ${file.type}. Only JPG, JPEG, PNG allowed.`,
        timestamp: Date.now()
      });
      store.setStatus('running');
      const nextId = this.resolveInputRoute(currentNode.id, '', store.variables);
      if (nextId) await this.executeNode(nextId);
      else store.setStatus('completed');
      return;
    }

    if (file.size > MAX_SIZE) {
      store.updateVariable(`${varName}.url`, null);
      store.updateVariable(`${varName}.filename`, file.name);
      store.updateVariable(`${varName}.mimeType`, file.type);
      store.updateVariable(`${varName}.error`, 'file_too_large');
      store.addHistoryItem({
        id: Math.random().toString(36).substr(2, 9),
        nodeId: store.currentNodeId || '',
        sender: 'system',
        type: 'text',
        content: `❌ File too large. Max size is 5MB.`,
        timestamp: Date.now()
      });
      store.setStatus('running');
      const nextId = this.resolveInputRoute(currentNode.id, '', store.variables);
      if (nextId) await this.executeNode(nextId);
      else store.setStatus('completed');
      return;
    }

    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    store.updateVariable(`${varName}.url`, dataUrl);
    store.updateVariable(`${varName}.filename`, file.name);
    store.updateVariable(`${varName}.mimeType`, file.type);
    store.updateVariable(`${varName}.error`, null);
    store.updateVariable('lastUploadedImage', dataUrl);

    store.addHistoryItem({
      id: Math.random().toString(36).substr(2, 9),
      nodeId: store.currentNodeId || '',
      sender: 'user',
      type: 'upload',
      content: file.name,
      uploadPreview: dataUrl,
      timestamp: Date.now()
    });

    store.setStatus('running');
    const nextId = this.resolveInputRoute(currentNode.id, '', store.variables);
    if (nextId) await this.executeNode(nextId);
    else store.setStatus('completed');
  }

  async handleFormSubmit(values) {
    const store = useChatStore.getState();
    if (store.status !== 'paused') return;

    const summary = Object.entries(values).map(([k, v]) => `${k}: ${v}`).join('\n');
    store.addHistoryItem({
      id: Math.random().toString(36).substr(2, 9),
      nodeId: store.currentNodeId || '',
      sender: 'user',
      type: 'text',
      content: 'Form submitted:\n' + summary,
      timestamp: Date.now()
    });

    Object.entries(values).forEach(([key, value]) => store.updateVariable(key, value));
    store.setStatus('running');

    const nextId = this.getFirstEdge(store.currentNodeId || '');
    if (nextId) await this.executeNode(nextId);
    else store.setStatus('completed');
  }

  async handleButtonChoice(targetNodeId, label, url, buttonId = null) {
    const store = useChatStore.getState();
    if (store.status !== 'paused') return;

    store.addHistoryItem({
      id: Math.random().toString(36).substr(2, 9),
      nodeId: store.currentNodeId || '',
      sender: 'user',
      type: 'text',
      content: label,
      timestamp: Date.now()
    });

    store.updateVariable('lastClickedButton', label);

    if (targetNodeId === 'add_url') {
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      store.setStatus('completed');
      return;
    }

    store.setStatus('running');

    if (targetNodeId) {
      await this.executeNode(targetNodeId);
      return;
    }

    const currentNodeId = store.currentNodeId;
    if (currentNodeId) {
      const nextId = (buttonId ? this.getEdgeByHandle(currentNodeId, buttonId) : null) || this.getFirstEdge(currentNodeId);
      if (nextId) await this.executeNode(nextId);
      else store.setStatus('completed');
    }
  }

  async executeNode(nodeId) {
    const store = useChatStore.getState();
    store.setCurrentNodeId(nodeId);
    store.addVisitedNodeId(nodeId);

    const node = this.nodes.find(n => n.id === nodeId);
    if (!node) { store.setStatus('completed'); return; }

    const data = node.data;

    switch (data.type) {
      case 'start': {
        const next = this.getFirstEdge(nodeId);
        if (next) await this.executeNode(next);
        else store.setStatus('completed');
        break;
      }
      case 'function': {
        // formulas run on the server, same as on the website
        const result = await this.runOnServer(nodeId, data, store);
        (result.errors || []).forEach((err) => store.addHistoryItem({
          id: Math.random().toString(36).substr(2, 9),
          nodeId,
          sender: 'system',
          type: 'text',
          content: `Function error: ${err}`,
          timestamp: Date.now()
        }));
        const next = this.getEdgeByHandle(nodeId, result.branch) ?? this.getFirstEdge(nodeId);
        if (next) await this.executeNode(next);
        else store.setStatus('completed');
        break;
      }

      case 'message': {
        await this.renderMessages(nodeId, data, store);
        const hasButtons = data.messages?.some(m => m.buttons && m.buttons.length > 0);
        const outgoing = this.edges.filter(e => e.source === nodeId);
        const hasKeywordChildren = outgoing.some(e => {
          const t = this.nodes.find(n => n.id === e.target);
          return t?.data.type === 'input' && t.data.keywords;
        });

        if (hasButtons || hasKeywordChildren) {
          store.setStatus('paused');
        } else {
          const next = this.getFirstEdge(nodeId);
          if (next) await this.executeNode(next);
          else store.setStatus('completed');
        }
        break;
      }

      case 'input': {
        if (data.keywords) {
          await this.renderMessages(nodeId, data, store);
          if (data.inputPrompt) {
            await new Promise(r => setTimeout(r, 600));
            store.addHistoryItem({
              id: Math.random().toString(36).substr(2, 9),
              nodeId,
              sender: 'bot',
              type: 'text',
              content: this.substituteVariables(data.inputPrompt, store.variables),
              timestamp: Date.now()
            });
          }
          store.setStatus('paused');
          break;
        }

        if (data.inputPrompt) {
          await new Promise(r => setTimeout(r, 600));
          store.addHistoryItem({
            id: Math.random().toString(36).substr(2, 9),
            nodeId,
            sender: 'bot',
            type: 'text',
            content: this.substituteVariables(data.inputPrompt, store.variables),
            timestamp: Date.now()
          });
        }

        if (data.inputType === 'form') {
          await new Promise(r => setTimeout(r, 600));
          store.addHistoryItem({
            id: Math.random().toString(36).substr(2, 9),
            nodeId,
            sender: 'bot',
            type: 'form',
            formFields: data.formFields?.map(f => ({
              ...f,
              label: this.substituteVariables(f.label, store.variables)
            })),
            timestamp: Date.now()
          });
        }

        if (data.inputMode === 'image' || data.inputMode === 'text+image') {
          store.addHistoryItem({
            id: Math.random().toString(36).substr(2, 9),
            nodeId,
            sender: 'bot',
            type: 'text',
            content: '__upload_widget__',
            timestamp: Date.now()
          });
        }

        const next = this.getFirstEdge(nodeId);
        if (next) {
          store.setStatus('paused');
        } else {
          store.setStatus('completed');
        }
        break;
      }

      case 'condition': {
        const nextId = this.resolveConditionRoute(nodeId, data, store.variables);
        if (nextId) await this.executeNode(nextId);
        else store.setStatus('completed');
        break;
      }

      case 'webhook_result': {
        await this.renderMessages(nodeId, data, store);
        const next = this.getFirstEdge(nodeId);
        if (next) await this.executeNode(next);
        else store.setStatus('completed');
        break;
      }

      case 'delay': {
        await new Promise(r => setTimeout(r, data.delayMs || 1000));
        const next = this.getFirstEdge(nodeId);
        if (next) await this.executeNode(next);
        else store.setStatus('completed');
        break;
      }

      case 'goto': {
        if (data.isFlowJump && data.targetFlowId) {
          try {
            const targetFlow = await api.getBotFlowDetail(data.targetFlowId);
            if (targetFlow) {
              let flowData = targetFlow;
              if (targetFlow.content && typeof targetFlow.content === 'string') {
                try {
                  flowData = JSON.parse(targetFlow.content);
                } catch (parseErr) {
                  console.error('Failed to parse flow content:', parseErr);
                  store.setStatus('completed');
                  break;
                }
              }
              if (flowData.nodes) {
                const savedGraph = { nodes: this.nodes, edges: this.edges };
                this.updateGraph(flowData.nodes, flowData.edges || []);
                const startNode = flowData.nodes.find(n => n.data?.type === 'start');
                if (startNode) {
                  await this.executeNode(startNode.id);
                } else {
                  this.updateGraph(savedGraph.nodes, savedGraph.edges);
                  store.setStatus('completed');
                }
              } else {
                store.setStatus('completed');
              }
            } else {
              store.setStatus('completed');
            }
          } catch (err) {
            console.error('Failed to load target flow:', err);
            store.setStatus('completed');
          }
          break;
        }
        if (data.targetNodeId) await this.executeNode(data.targetNodeId);
        else store.setStatus('completed');
        break;
      }

      case 'attribute': {
        const attrName = data.attributeName === 'custom' ? data.variableName : data.attributeName;
        if (attrName && data.attributeValue !== undefined) {
          store.updateVariable(attrName, data.attributeValue);
        }
        const next = this.getFirstEdge(nodeId);
        if (next) await this.executeNode(next);
        else store.setStatus('completed');
        break;
      }

      case 'ai_agent': {
        await this.executeAiTurn(nodeId, data, store);
        break;
      }

      case 'action': {
        // the webhook is called by the server, same as on the website (no CORS, credentials never in the browser)
        const result = await this.runOnServer(nodeId, data, store);
        const apiResult = result.variables && result.variables.webhookResponse;
        if (result.branch === 'success' && apiResult) {
          store.addHistoryItem({
            id: Math.random().toString(36).substr(2, 9),
            nodeId,
            sender: 'bot',
            type: 'api_response',
            apiData: apiResult.value === undefined ? apiResult : undefined,
            content: apiResult.value !== undefined ? String(apiResult.value) : undefined,
            timestamp: Date.now()
          });
        }
        const next = this.getEdgeByHandle(nodeId, result.branch === 'success' ? 'success' : 'failure') ?? this.getFirstEdge(nodeId);
        if (next) await this.executeNode(next);
        else store.setStatus('completed');
        break;
      }

      case 'escalate': {
        store.addHistoryItem({
          id: Math.random().toString(36).substr(2, 9),
          nodeId,
          sender: 'system',
          type: 'text',
          content: '🆘 Escalating to human assistant...',
          timestamp: Date.now()
        });

        const sessionId = store.sessionId;
        const reason = data.reason || 'User requested human assistant via bot flow';
        const result = await api.requestHandoff(sessionId, reason);

        await new Promise(r => setTimeout(r, 400));

        if (result && result.status === 'active' && result.assigned_to_name) {
          store.addHistoryItem({
            id: Math.random().toString(36).substr(2, 9),
            nodeId,
            sender: 'bot',
            type: 'text',
            content: `I have connected you to human agent **${result.assigned_to_name}**. They will assist you now.`,
            timestamp: Date.now()
          });
        } else {
          store.addHistoryItem({
            id: Math.random().toString(36).substr(2, 9),
            nodeId,
            sender: 'bot',
            type: 'text',
            content: result?.message || 'A human agent has been requested. Someone will be with you shortly.',
            timestamp: Date.now()
          });
        }

        store.setStatus('paused');
        break;
      }

      case 'integration': {
        const intConfig = data.integrationConfig || {};
        if (intConfig.integrationType === 'freshchat' && intConfig.action === 'handoff') {
          store.addHistoryItem({
            id: Math.random().toString(36).substr(2, 9),
            nodeId,
            sender: 'system',
            type: 'text',
            content: '🔌 Connecting you to a Freshchat human agent...',
            timestamp: Date.now()
          });

          const sessionId = store.sessionId || 'simulator_session_' + Math.random().toString(36).substr(2, 9);
          const reason = 'Freshchat handoff via integration node (Test Simulator)';
          
          try {
            const result = await api.requestHandoff(sessionId, reason, 'freshchat');
            await new Promise(r => setTimeout(r, 400));
            if (result && result.status === 'requested') {
              store.addHistoryItem({
                id: Math.random().toString(36).substr(2, 9),
                nodeId,
                sender: 'bot',
                type: 'text',
                content: 'A human agent has been requested on Freshchat. They will be with you shortly.',
                timestamp: Date.now()
              });
            } else {
              store.addHistoryItem({
                id: Math.random().toString(36).substr(2, 9),
                nodeId,
                sender: 'bot',
                type: 'text',
                content: 'Freshchat handoff could not be completed at this time.',
                timestamp: Date.now()
              });
            }
          } catch (e) {
            console.error('Error in freshchat simulation handoff:', e);
            store.addHistoryItem({
              id: Math.random().toString(36).substr(2, 9),
              nodeId,
              sender: 'bot',
              type: 'text',
              content: 'Failed to connect to Freshchat due to a network error.',
              timestamp: Date.now()
            });
          }
          store.setStatus('paused');
        } else {
          const next = this.getFirstEdge(nodeId);
          if (next) await this.executeNode(next);
          else store.setStatus('completed');
        }
        break;
      }

      default:
        store.setStatus('completed');
    }
  }

  // Runs a webhook/action, AI or function step on the server (admin token; the unsaved step is sent along)
  async runOnServer(nodeId, data, store) {
    try {
      const token = sessionStorage.getItem('user');
      const maybeToken = token ? JSON.parse(token).token : null;
      const agentId = localStorage.getItem('activeAiAgentId');
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/easyflow/run-node`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(maybeToken ? { 'Authorization': `Bearer ${maybeToken}` } : {}),
          ...(agentId ? { 'X-AI-Agent-ID': agentId } : {}),
        },
        body: JSON.stringify({ node_id: nodeId, node: { data }, variables: store.variables }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        store.addHistoryItem({
          id: Math.random().toString(36).substr(2, 9),
          nodeId,
          sender: 'system',
          type: 'text',
          content: `Step failed: ${result.detail || `HTTP ${res.status}`}`,
          timestamp: Date.now()
        });
        return { branch: 'failure', variables: {} };
      }
      Object.entries(result.variables || {}).forEach(([key, value]) => store.updateVariable(key, value));
      return result;
    } catch (e) {
      return { branch: 'failure', variables: {} };
    }
  }

  async executeAiTurn(nodeId, data, store) {
    const DEFAULT_AI = { systemPromptOverride: '', userInput: '{{lastUserInput}}', outputVariable: 'aiResponse' };
    const aiCfg = { ...DEFAULT_AI, ...(data.aiConfig || {}) };
    const sub = (s) => this.substituteVariables(s || '', store.variables);

    const resolvedInput = sub(aiCfg.userInput);
    if (!resolvedInput.trim()) {
      store.setStatus('paused');
      return;
    }

    let routingIntent = null;
    let routingNextNode = null;
    let aiResponseText = '';

    try {
      const token = sessionStorage.getItem('user');
      const maybeToken = token ? JSON.parse(token).token : null;
      const agentId = localStorage.getItem('activeAiAgentId');
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/widget/ai-node/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(maybeToken ? { 'Authorization': `Bearer ${maybeToken}` } : {}),
          ...(agentId ? { 'X-AI-Agent-ID': agentId } : {}),
        },
        body: JSON.stringify({
          userInput: resolvedInput,
          systemPromptOverride: sub(aiCfg.systemPromptOverride) || undefined,
          sessionId: store.sessionId,
          conversationHistory: store.aiConversationHistory,
          temperature: aiCfg.temperature,
          maxTokens: aiCfg.maxTokens,
          topP: aiCfg.topP,
        }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        store.addHistoryItem({
          id: Math.random().toString(36).substr(2, 9),
          nodeId,
          sender: 'system',
          type: 'text',
          content: 'AI failed: ' + (errBody.detail || `HTTP ${res.status}`),
          timestamp: Date.now(),
        });
        store.setStatus('paused');
        return;
      }
      const result = await res.json();

      if (result.success && result.data?.output) {
        aiResponseText = result.data.output;
        routingIntent = result.data.intent || null;
        routingNextNode = result.data.nextNode ?? null;

        store.updateVariable(aiCfg.outputVariable || 'aiResponse', aiResponseText);
        store.updateVariable('lastAiIntent', routingIntent);
        store.updateVariable('lastAiNextNode', routingNextNode);

        store.addHistoryItem({
          id: Math.random().toString(36).substr(2, 9),
          nodeId,
          sender: 'bot',
          type: 'text',
          content: aiResponseText,
          timestamp: Date.now(),
        });
        if (Array.isArray(result.data.conversationHistory)) {
          store.setAiConversationHistory(result.data.conversationHistory);
        }
      } else {
        store.addHistoryItem({
          id: Math.random().toString(36).substr(2, 9),
          nodeId,
          sender: 'system',
          type: 'text',
          content: 'AI request failed: ' + (result.error || 'Unknown error'),
          timestamp: Date.now(),
        });
      }
    } catch {
      store.addHistoryItem({
        id: Math.random().toString(36).substr(2, 9),
        nodeId,
        sender: 'system',
        type: 'text',
        content: 'AI service unavailable. Make sure the server is running.',
        timestamp: Date.now(),
      });
    }

    if (routingNextNode != null) {
      const directMatch = this.nodes.find(n => n.id === routingNextNode);
      if (directMatch) {
        await this.executeNode(directMatch.id);
        return;
      }
    }

    store.setStatus('paused');
  }

  resolveInputRoute(sourceId, text, variables) {
    const outgoing = this.edges.filter(e => e.source === sourceId);
    if (outgoing.length === 0) return null;

    const lower = text.toLowerCase();
    let fallback = null;

    for (const edge of outgoing) {
      const target = this.nodes.find(n => n.id === edge.target);
      if (!target) continue;
      const tData = target.data;

      if (tData.type === 'input' && tData.keywords) {
        const keywords = tData.keywords.split(',').map(k => k.trim().toLowerCase()).filter(Boolean);
        if (keywords.some(k => lower.includes(k))) return target.id;
      } else if (tData.type === 'condition') {
        const matched = this.resolveConditionRoute(target.id, tData, variables);
        if (matched) return target.id;
      } else {
        if (fallback === null) fallback = target.id;
      }
    }

    const hasKeywordChildren = outgoing.some(e => {
      const t = this.nodes.find(n => n.id === e.target);
      return t?.data.type === 'input' && t.data.keywords;
    });

    return hasKeywordChildren ? null : fallback;
  }

  resolveConditionRoute(nodeId, data, variables) {
    const branches = data.branches || [];

    for (const branch of branches) {
      if (this.evaluateRule(branch.rule, variables)) {
        if (branch.targetNodeId) return branch.targetNodeId;
        const edgeTarget = this.getEdgeByHandle(nodeId, branch.id);
        if (edgeTarget) return edgeTarget;
      }
    }

    if (data.defaultTargetNodeId) return data.defaultTargetNodeId;

    return null;
  }

  getFirstEdge(sourceId) {
    const edge = this.edges.find(e => e.source === sourceId);
    return edge ? edge.target : null;
  }

  getEdgeByHandle(sourceId, handle) {
    const edge = this.edges.find(e => e.source === sourceId && e.sourceHandle === handle);
    return edge ? edge.target : null;
  }

  async renderMessages(nodeId, data, store) {
    if (!data.messages) return;
    for (const msg of data.messages) {
      await new Promise(r => setTimeout(r, 600));
      let content = msg.content || '';
      if (msg.type === 'random_text' && msg.items?.length) {
        content = msg.items[Math.floor(Math.random() * msg.items.length)];
      }
      store.addHistoryItem({
        id: Math.random().toString(36).substr(2, 9),
        nodeId,
        sender: 'bot',
        type: msg.type,
        content: this.substituteVariables(content, store.variables),
        items: msg.items?.map(i => this.substituteVariables(i, store.variables)),
        buttons: msg.buttons,
        timestamp: Date.now()
      });
    }
  }

  substituteVariables(text, variables) {
    if (!text) return text;
    return text.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
      let key = path.trim();
      if (!key) return match;

      if (key.startsWith('webhook.') && !key.startsWith('webhookResponse.')) {
        key = 'webhookResponse.' + key.slice('webhook.'.length);
      }

      if (key.includes('.')) {
        const deepVal = key.split('.').reduce((o, k) => {
          if (o === null || o === undefined) return undefined;
          return typeof o === 'object' ? o[k] : undefined;
        }, variables);
        if (deepVal !== undefined && deepVal !== null) return String(deepVal);

        const flatVal = variables[key];
        if (flatVal !== undefined && flatVal !== null) return String(flatVal);

        return match;
      }

      const val = variables[key];
      return val !== undefined && val !== null ? String(val) : match;
    });
  }

  evaluateRule(rule, variables) {
    if (!rule) return false;
    const { field, operator, value } = rule;

    let resolvedField = field;
    if (resolvedField.startsWith('webhook.') && !resolvedField.startsWith('webhookResponse.')) {
      resolvedField = 'webhookResponse.' + resolvedField.slice('webhook.'.length);
    }

    const varValue = resolvedField.includes('.')
      ? resolvedField.split('.').reduce((o, k) => o?.[k], variables)
      : variables[resolvedField];

    const strVal = String(varValue ?? '');
    const ruleVal = String(value ?? '');

    switch (operator) {
      case 'equals':            return strVal === ruleVal;
      case 'not_equals':        return strVal !== ruleVal;
      case 'contains':          return strVal.toLowerCase().includes(ruleVal.toLowerCase());
      case 'starts_with':       return strVal.toLowerCase().startsWith(ruleVal.toLowerCase());
      case 'ends_with':         return strVal.toLowerCase().endsWith(ruleVal.toLowerCase());
      case 'greater_than':      return Number(varValue) > Number(value);
      case 'less_than':         return Number(varValue) < Number(value);
      case 'is_empty':          return !varValue || strVal.trim() === '';
      case 'is_not_empty':      return !!varValue && strVal.trim() !== '';
      case 'image_exists':      return !!varValue && strVal !== 'null' && strVal.trim() !== '';
      case 'upload_missing':    return !varValue || strVal === 'null' || strVal.trim() === '';
      case 'invalid_file_type': return variables[field.replace(/\.url$/, '.error')] === 'invalid_file_type';
      case 'matches_regex': {
        try { return new RegExp(ruleVal).test(strVal); } catch { return false; }
      }
      default: return false;
    }
  }
    }
